/**
 * In-memory fixture / replay transport.
 *
 * Implements the same {@link TransportClient} contract as the live
 * {@link QvWebSocketClient} but synthesises deterministic OHLC bars locally, so
 * the web app can run fully offline before `qv-data` exists. It emits a bulk
 * load per chart group, then a bounded number of realtime append frames on a
 * timer — exercising exactly the callback paths a live server drives.
 *
 * Determinism: bars come from a seeded PRNG (no unseeded `Math.random`), so a
 * given `{seed, symbol}` always produces the same series.
 */

import {
  diffChartGroup,
  OHLCV_COLUMNS,
  type ChartGroupColumns,
} from "./columns.js";
import type { IndicatorRequest, ChartGroupRequest } from "./request.js";
import { chartGroupSubscriptionKey } from "./subscription.js";
import type {
  ConnectionStatus,
  TransportClient,
  TransportListeners,
} from "./client.js";

/** Options controlling the synthetic feed. */
export interface FixtureTransportOptions {
  /** Listener callbacks (same shape as the live client). */
  listeners?: TransportListeners;

  /** Bars in the initial bulk load per group. Default 120. */
  bars?: number;

  /** Number of realtime append frames to stream after the bulk load. Default 5. */
  streamFrames?: number;

  /** Milliseconds between append frames. Default 1000. */
  intervalMs?: number;

  /** Seconds between bars. Default 86400 (daily). */
  stepSeconds?: number;

  /** Unix-seconds timestamp of the first bar. Default 1_700_000_000. */
  startTime?: number;

  /** PRNG seed for reproducible series. Default 1. */
  seed?: number;

  /** Injectable timers (default global `setInterval`/`clearInterval`). */
  setInterval?: (fn: () => void, ms: number) => unknown;
  clearInterval?: (handle: unknown) => void;
}

interface GroupState {
  cg: ChartGroupRequest;
  aliases: string[];
  snapshot: ChartGroupColumns;
  price: number;
  t: number;
}

/** Deterministic mulberry32 PRNG → floats in [0, 1). */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Stable small integer from a string (used to vary indicator windows). */
function hashWindow(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (Math.imul(h, 31) + s.charCodeAt(i)) | 0;
  return 3 + (Math.abs(h) % 18); // window in [3, 20]
}

/** Flatten a chart group's indicator aliases (shortname fallback). */
function groupAliases(cg: ChartGroupRequest): string[] {
  const out: string[] = [];
  for (const ind of cg.indicators) {
    const aliases = ind.aliases ?? [];
    if (aliases.length > 0) out.push(...aliases);
    else out.push(ind.shortname);
  }
  return [...new Set(out)];
}

export class FixtureTransportClient implements TransportClient {
  private readonly opts: Required<
    Omit<FixtureTransportOptions, "listeners" | "setInterval" | "clearInterval">
  > &
    Pick<FixtureTransportOptions, "listeners">;
  private readonly setIntervalFn: (fn: () => void, ms: number) => unknown;
  private readonly clearIntervalFn: (handle: unknown) => void;

  private _status: ConnectionStatus = "idle";
  private timer: unknown = null;
  private framesLeft = 0;
  private readonly groups = new Map<string, GroupState>();

  constructor(options: FixtureTransportOptions = {}) {
    this.opts = {
      bars: options.bars ?? 120,
      streamFrames: options.streamFrames ?? 5,
      intervalMs: options.intervalMs ?? 1000,
      stepSeconds: options.stepSeconds ?? 86_400,
      startTime: options.startTime ?? 1_700_000_000,
      seed: options.seed ?? 1,
      listeners: options.listeners,
    };
    const g = globalThis as {
      setInterval?: (fn: () => void, ms: number) => unknown;
      clearInterval?: (h: unknown) => void;
    };
    this.setIntervalFn =
      options.setInterval ?? ((fn, ms) => g.setInterval!(fn, ms));
    this.clearIntervalFn =
      options.clearInterval ?? ((h) => g.clearInterval!(h));
  }

  get status(): ConnectionStatus {
    return this._status;
  }

  connect(): Promise<void> {
    this.setStatus("open");
    return Promise.resolve();
  }

  request(req: IndicatorRequest): Promise<void> {
    if (this._status !== "open") this.setStatus("open");

    // Build + emit a bulk load per chart group.
    for (const cg of req.chartGroups) {
      const state = this.buildInitial(cg);
      this.groups.set(String(cg.chartGroupId), state);
      this.opts.listeners?.onBulkLoad?.({
        subscriptionKey: chartGroupSubscriptionKey(cg),
        chartGroupId: state.snapshot.chartGroupId,
        columns: state.snapshot,
      });
    }

    // Start (or restart) the realtime append timer.
    this.stopTimer();
    this.framesLeft = this.opts.streamFrames;
    if (this.framesLeft > 0 && this.groups.size > 0) {
      this.timer = this.setIntervalFn(() => this.tick(), this.opts.intervalMs);
    }
    return Promise.resolve();
  }

  disconnect(): void {
    this.stopTimer();
    this.groups.clear();
    this.setStatus("closed");
  }

  // ── generation ──────────────────────────────────────────────────────────

  private buildInitial(cg: ChartGroupRequest): GroupState {
    const aliases = groupAliases(cg);
    const rnd = mulberry32(this.opts.seed + hashWindow(cg.symbol));
    const n = this.opts.bars;

    const x: number[] = new Array(n);
    const open: number[] = new Array(n);
    const high: number[] = new Array(n);
    const low: number[] = new Array(n);
    const close: number[] = new Array(n);
    const volume: number[] = new Array(n);

    let price = 100;
    let t = this.opts.startTime;
    for (let i = 0; i < n; i++) {
      const o = price;
      const drift = (rnd() - 0.5) * 2; // random walk step
      const c = Math.max(1, o + drift);
      const hi = Math.max(o, c) + rnd();
      const lo = Math.min(o, c) - rnd();
      x[i] = t;
      open[i] = round2(o);
      high[i] = round2(hi);
      low[i] = round2(Math.max(0.5, lo));
      close[i] = round2(c);
      volume[i] = Math.round(1_000 + rnd() * 9_000);
      price = c;
      t += this.opts.stepSeconds;
    }

    const columns: Record<string, number[]> = { open, high, low, close, volume };
    for (const alias of aliases) {
      columns[alias] = smoothed(close, hashWindow(alias));
    }

    const snapshot: ChartGroupColumns = {
      chartGroupId: String(cg.chartGroupId),
      status: "ok",
      x,
      xKind: "seconds",
      columns,
      labels: { symbol: cg.symbol.toUpperCase() },
      symbol: cg.symbol.toUpperCase(),
    };
    return { cg, aliases, snapshot, price, t };
  }

  /** Append one bar to every group and emit diff-based append frames. */
  private tick(): void {
    for (const [id, state] of this.groups) {
      const next = this.appendBar(state);
      const deltas = diffChartGroup(state.snapshot, next);
      state.snapshot = next;
      if (deltas.length > 0) {
        this.opts.listeners?.onAppend?.({
          subscriptionKey: chartGroupSubscriptionKey(state.cg),
          chartGroupId: id,
          deltas,
        });
      }
    }
    this.framesLeft -= 1;
    if (this.framesLeft <= 0) this.stopTimer();
  }

  private appendBar(state: GroupState): ChartGroupColumns {
    const rnd = mulberry32(
      this.opts.seed + hashWindow(state.cg.symbol) + state.t,
    );
    const prev = state.snapshot;
    const o = state.price;
    const c = Math.max(1, o + (rnd() - 0.5) * 2);
    const hi = Math.max(o, c) + rnd();
    const lo = Math.max(0.5, Math.min(o, c) - rnd());

    const columns: Record<string, number[]> = {};
    for (const key of Object.keys(prev.columns)) {
      columns[key] = prev.columns[key]!.slice();
    }
    columns["open"]!.push(round2(o));
    columns["high"]!.push(round2(hi));
    columns["low"]!.push(round2(lo));
    columns["close"]!.push(round2(c));
    columns["volume"]!.push(Math.round(1_000 + rnd() * 9_000));

    const closeCol = columns["close"]!;
    for (const alias of state.aliases) {
      columns[alias] = smoothed(closeCol, hashWindow(alias));
    }

    state.price = c;
    const nextX = prev.x[prev.x.length - 1]! + this.opts.stepSeconds;
    state.t = nextX;

    const snapshot: ChartGroupColumns = {
      chartGroupId: prev.chartGroupId,
      status: "ok",
      x: [...prev.x, nextX],
      xKind: "seconds",
      columns,
      labels: prev.labels,
      symbol: prev.symbol,
    };
    return snapshot;
  }

  private stopTimer(): void {
    if (this.timer != null) {
      this.clearIntervalFn(this.timer);
      this.timer = null;
    }
  }

  private setStatus(status: ConnectionStatus): void {
    if (this._status === status) return;
    this._status = status;
    this.opts.listeners?.onStatus?.(status);
  }
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Trailing simple moving average of `src` over `window` (nulls filled forward). */
function smoothed(src: number[], window: number): number[] {
  const out = new Array<number>(src.length);
  let sum = 0;
  for (let i = 0; i < src.length; i++) {
    sum += src[i]!;
    if (i >= window) sum -= src[i - window]!;
    const count = Math.min(i + 1, window);
    out[i] = round2(sum / count);
  }
  return out;
}

export { OHLCV_COLUMNS };
